
  import { Component, Prop, Vue, Watch } from "vue-property-decorator";
  import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";
  import UiButton from "@/components/button/ui-button.vue";
  import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
  import DefaultRadioButton from "@/components/common/DefaultRadioButton.vue";
  import ContractorsMultiselect from "@/components/autocompleteMultiselects/ContractorsMultiselect.vue";
  import DocumentExtensionIcon from "@/components/documents/DocumentExtensionIcon.vue";
  import DocumentProjectAccessScope from "@/data/documentAccessScope/DocumentProjectAccessScope";
  import GET_DOCUMENT_PROJECT_ACCESS_SCOPE from "@/apollo/documents/GetDocumentProjectAccessScope.gql";
  import UPDATE_DOCUMENT_ACCESS_SCOPE from "@/apollo/documents/UpdateDocumentAccessScope.gql";
  import { Document_Contractor_Access, Document_Project } from "@/generated/graphql";
  import { NOTIFICATION } from "@/entities/Events";
  import Message from "@/data/notificationsMessages";
  import ProjectDocumentAccessScopePlaceholder
    from "@/components/skeletonPlaceholder/components/ProjectDocumentAccessScopePlaceholder.vue";
  import FormGroupError from "@/components/form/FormGroupError.vue";
  import ValidatableEmployeesMultiselect
    from "@/components/autocompleteMultiselects/ValidatableEmployeesMultiselect.vue";
  import Tooltip from "@/components/common/Tooltip.vue";
  import TooltipVariants from "@/entities/TooltipVariants";
  import Roles from "@/entities/Roles";

  @Component({
    components: {
      Tooltip,
      ValidatableEmployeesMultiselect,
      FormGroupError,
      ProjectDocumentAccessScopePlaceholder,
      DocumentExtensionIcon,
      ContractorsMultiselect,
      DefaultRadioButton,
      ModalSectionsDivider,
      UiButton,
      ModalFullScreen
    },
    apollo: {
      documentAccessScope: {
        query: GET_DOCUMENT_PROJECT_ACCESS_SCOPE,
        variables() {
          return {
            documentId: this.documentId,
            projectId: this.projectId,
          };
        },
        update(data: { document_project: Document_Project[] }): void {
          const {
            id,
            document,
            access_scope: accessScope,
          } = data.document_project[0];

          const contractorAccessList = document.document_contractor_accesses;

          this.documentProjectId = id;
          this.originalContractorAccessList = contractorAccessList;
          this.selectedContractorsIds = contractorAccessList.map(contractorAccessItem => contractorAccessItem.contractor_id);
          this.chosenAccessScope = accessScope ?? DocumentProjectAccessScope.MANAGEMENT;
          //@ts-ignore
          this.uploadedByUserRole = document.user.role;
        },
      },
    },
  })
  export default class ProjectDocumentAccessScopeModal extends Vue {
    @Prop({ type: String, required: true }) readonly documentId!: string;
    @Prop({ type: String, required: true }) readonly fileName!: string;
    @Prop({ type: String, required: true }) readonly documentExtension!: string;
    @Prop({ type: String, required: true }) readonly projectId!: string;

    documentProjectId: string = "";
    originalContractorAccessList: Document_Contractor_Access[] = [];
    selectedContractorsIds: string[] = [];
    chosenAccessScope: DocumentProjectAccessScope = DocumentProjectAccessScope.MANAGEMENT;
    processing: boolean = false;
    contractorSelectError: boolean = false;
    uploadedByUserRole: string = "";

    DocumentProjectAccessScope = DocumentProjectAccessScope;
    TooltipVariants = TooltipVariants;

    get isUploadedByAdmin(): boolean {
      return this.uploadedByUserRole === Roles.ADMIN;
    }

    get radioButtonClasses(): string {
      return "flex items-center py-2 space-x-1.5";
    }

    get radioButtonGroupName(): string {
      return "access_scope";
    }

    get isLoading(): boolean {
      return this.$apollo.queries.documentAccessScope.loading;
    }

    get contractorsConditions(): Record<string, any> {
      return {
        project_activities: {
          project_id: {
            _eq: this.projectId,
          },
        },
      };
    }

    get contractorsMultiselectDisabled(): boolean {
      return this.chosenAccessScope !== DocumentProjectAccessScope.SELECTED;
    }

    async updateAccessScope(): Promise<void> {
      const contractorSelected = this.selectedContractorsIds.length > 0;

      if (!contractorSelected && !this.contractorsMultiselectDisabled) {
        this.contractorSelectError = true;
        return;
      }

      this.processing = true;

      try {
        await this.updateScope();

        this.$emit("accessScopeUpdated");
        this.$eventBus.emit(
          NOTIFICATION,
          Message.successMessage(`The access scope for the ${this.fileName} document has been updated`)
        );
      } catch (_) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage("Something went wrong!"));
      } finally {
        this.processing = false;
      }
    }

    @Watch("selectedContractorsIds")
    onSelectedIdsChange(selectedContractorsIds: string[]): void {
      if (this.contractorSelectError) {
        this.contractorSelectError = selectedContractorsIds.length === 0;
      }
    }

    updateScope() {
      const accessScope = this.chosenAccessScope;
      const documentProjectId = this.documentProjectId;

      if (this.contractorsMultiselectDisabled) {
        return this.$apollo.mutate({
          mutation: UPDATE_DOCUMENT_ACCESS_SCOPE,
          variables: {
            accessScope,
            documentProjectId,
            addAccessContractors: [],
            deleteContractorAccessIds: this.originalContractorAccessList.map(({ id }) => id),
          },
        });
      }

      const deleteContractorAccessIds = this.originalContractorAccessList
        .filter(contractorAccess => this.selectedContractorsIds
          .every(selectedContractorId => selectedContractorId !== contractorAccess.contractor_id)
        )
        .map(({ id }) => id);

      const addAccessContractors = this.selectedContractorsIds
        .filter(contractorId => this.originalContractorAccessList
          .every(contractorAccess => contractorAccess.contractor_id !== contractorId)
        )
        .map(id => ({
          contractor_id: id,
          document_id: this.documentId,
        }));

      return this.$apollo.mutate({
        mutation: UPDATE_DOCUMENT_ACCESS_SCOPE,
        variables: {
          accessScope,
          documentProjectId,
          addAccessContractors,
          deleteContractorAccessIds,
        },
      });
    }

    isChecked(value: string): boolean {
      if (!this.chosenAccessScope) {
        return DocumentProjectAccessScope.MANAGEMENT === value;
      }
      return this.chosenAccessScope === value;
    }

  }
